/**
 * レコード単位編集のモーダル (追加 / 修正)
 * 設計: docs/design/v03-record-level-data-editing-detail.md §8-1
 *
 * インラインセル編集ではなく行モーダルにしている理由:
 *   - メニュー/食材の再解決と税込・税抜の再計算の結果を、保存前に対比表示する必要がある
 *   - 納品明細は10列あり、横スクロール中のセル編集は操作性が悪い
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { editableFieldsOf, formatCell, periodLabelOf, type FieldMeta } from '@/lib/data-record-fields';

export type RecordRow = Record<string, unknown> & { record_id?: string; version?: string | null };

/**
 * 公開ゲートへの影響を伝える共通バナー。
 * ⚠️ 追加・修正・削除・一括削除の「すべての」確認面に出すこと (Design CRITICAL-1)。
 *    どの操作でも該当 (店舗×月) が draft へ戻るため、編集モーダルだけに出すのでは不足する。
 */
export function PublishImpactWarning({ periods }: { periods: string[] }) {
  if (periods.length === 0) return null;
  return (
    <div className="flex gap-2 rounded-md border border-alert-border bg-alert-bg px-3 py-2.5 text-[13px] text-alert-text">
      <AlertTriangle size={16} className="shrink-0 mt-0.5" aria-hidden />
      <div className="space-y-1">
        <p className="font-bold">保存すると、次の期間の公開が取り消されます</p>
        <ul className="list-disc pl-4">
          {periods.map((p) => <li key={p}>{p}</li>)}
        </ul>
        <p>
          公開が取り消されると、経営ダッシュボードからこの期間の数値が見えなくなり、ダウンロードもできなくなります。
          再び見えるようにするには、公開管理画面から改めて公開の操作が必要です。
        </p>
      </div>
    </div>
  );
}

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  dataType: string;
  storeLabel: string;
  /** edit のとき: 編集対象の現在値。create のとき: 初期値 (対象月など) */
  initial: RecordRow;
  /** 影響する期間 (例: ['2026年1月']) */
  impactedPeriods: string[];
  saving: boolean;
  errorMessage: string | null;
  /** values はフォーム全体、changedNames は初期値から実際に変わった項目名。
   *  修正時に全項目を送るとサーバ側の「明示指定かどうか」の判定が壊れるため、
   *  呼び出し側は changedNames で絞ってから PATCH すること (§3-3 の派生値ルール)。 */
  onSubmit: (values: Record<string, string>, changedNames: string[]) => void;
  onClose: () => void;
};

const toInputValue = (field: FieldMeta, v: unknown): string => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  // GET /data/records は日付を yyyy/mm/dd で返すため input[type=date] 用にハイフンへ揃える
  if (field.type === 'date') return s.slice(0, 10).replace(/\//g, '-');
  if (field.type === 'month') return s.slice(0, 7).replace(/\//g, '-');
  if (field.name === 'tax_rate' && s !== '') {
    const n = Number(s);
    return Number.isFinite(n) ? n.toFixed(2) : '';
  }
  return s;
};

export function RecordEditModal({
  open, mode, dataType, storeLabel, initial, impactedPeriods, saving, errorMessage, onSubmit, onClose,
}: Props) {
  const fields = useMemo(() => editableFieldsOf(dataType), [dataType]);
  const [values, setValues] = useState<Record<string, string>>({});
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const next: Record<string, string> = {};
    for (const f of fields) next[f.name] = toInputValue(f, initial[f.name]);
    setValues(next);
  }, [open, fields, initial]);

  // 税率が「不明」の行 (migration 0042 の backfill で確定できなかった少額行) は
  // 金額を再計算できないため、明示選択を必須にする (設計 §1-2 / §3-3 の 422)
  const taxRateUnknown = fields.some((f) => f.name === 'tax_rate')
    && (initial.tax_rate === null || initial.tax_rate === undefined || initial.tax_rate === '');

  const changed = useMemo(() => fields
    .filter((f) => values[f.name] !== undefined && values[f.name] !== toInputValue(f, initial[f.name]))
    .map((f) => ({
      field: f,
      before: formatCell(f, initial[f.name]),
      after: values[f.name] === '' ? '−' : (f.name === 'tax_rate' ? `${Math.round(Number(values[f.name]) * 100)}%` : values[f.name]),
    })), [fields, values, initial]);

  // ⚠️ 影響期間は「編集中の値」から都度算出する (Design CRITICAL-2 / CRITICAL-3)。
  //    親から渡された値だけを使うと ①追加時に絞込が「全期間」だと警告が丸ごと消える
  //    ②モーダル内で対象月を変えても旧月のままになり、月跨ぎ修正で前後2ヶ月が出ない。
  const livePeriods = useMemo(() => [...new Set([
    periodLabelOf(dataType, initial),      // 変更前の月 (create では通常 null)
    periodLabelOf(dataType, values),       // 変更後の月
    ...impactedPeriods,
  ].filter((v): v is string => !!v))], [dataType, initial, values, impactedPeriods]);

  const canSubmit = !saving
    && fields.every((f) => !f.required || (values[f.name] ?? '').trim() !== '')
    && (!taxRateUnknown || (values.tax_rate ?? '') !== '');

  return (
    <Modal
      open={open}
      title={mode === 'create' ? 'データを追加' : 'データを修正'}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={saving}
            className="h-10 px-5 rounded-md bg-white text-text-body text-[13px] font-extrabold btn-press-secondary hover:bg-surface-sunken disabled:opacity-45 focus-ring min-h-[44px] sm:min-h-[40px]">
            キャンセル
          </button>
          <button type="button" onClick={() => onSubmit(values, changed.map((c) => c.field.name))} disabled={!canSubmit}
            className="h-10 px-5 rounded-md bg-brand-red hover:bg-brand-red-hover text-white text-[13px] font-extrabold btn-press-primary disabled:opacity-45 disabled:cursor-not-allowed focus-ring min-h-[44px] sm:min-h-[40px]">
            {saving ? '保存中…' : '保存'}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="text-[13px] text-text-muted">対象店舗: <span className="font-bold text-text-body">{storeLabel}</span></p>

        <PublishImpactWarning periods={livePeriods} />

        {taxRateUnknown && (
          <div className="flex gap-2 rounded-md border border-surface-line bg-surface-sunken px-3 py-2.5 text-[13px] text-text-body">
            <Info size={16} className="shrink-0 mt-0.5" aria-hidden />
            <p>
              この行は取込時に税率が保存されておらず、金額から自動判別もできませんでした。
              金額を正しく計算するため、税率を選んでください。
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fields.map((f, idx) => (
            <label key={f.name} className="block">
              <span className="block text-[12px] font-bold text-text-muted mb-1">
                {f.label}{f.required && <span className="text-text-sub ml-0.5" aria-hidden>*</span>}
                {f.required && <span className="sr-only">（必須）</span>}
              </span>
              {f.type === 'select' ? (
                <Select value={values[f.name] ?? ''} onValueChange={(v) => setValues((p) => ({ ...p, [f.name]: v }))}>
                  <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選択してください" /></SelectTrigger>
                  <SelectContent>
                    {(f.options ?? []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <input
                  ref={idx === 0 ? firstFieldRef : undefined}
                  type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : f.type === 'month' ? 'month' : 'text'}
                  value={values[f.name] ?? ''}
                  placeholder={f.placeholder}
                  onChange={(e) => setValues((p) => ({ ...p, [f.name]: e.target.value }))}
                  className="w-full min-h-[44px] px-3 rounded-md border border-surface-line text-[13px] focus-ring"
                />
              )}
              {f.hint && <span className="block text-[11px] text-text-muted mt-1">{f.hint}</span>}
            </label>
          ))}
        </div>

        {mode === 'edit' && changed.length > 0 && (
          <div className="rounded-md border border-surface-line bg-surface-sunken p-3">
            <p className="text-[12px] font-bold text-text-muted mb-2">変更内容</p>
            <ul className="space-y-1 text-[13px]">
              {changed.map((ch) => (
                <li key={ch.field.name} className="flex flex-wrap gap-1">
                  <span className="text-text-muted">{ch.field.label}:</span>
                  <span className="line-through text-text-muted">{ch.before}</span>
                  <span aria-hidden>→</span>
                  <span className="font-bold">{ch.after}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-text-muted mt-2">
              税込・税抜の金額、メニューや食材に紐づく情報は保存時に再計算されます。
            </p>
          </div>
        )}

        {errorMessage && (
          <p role="alert" className="rounded-md border border-alert-border bg-alert-bg px-3 py-2 text-[13px] text-alert-text">
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}
